import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import './ProfileAvatarCropper.css';

const FRAME_SIZE = 280;
const OUTPUT_SIZE = 512;
const MIN_ZOOM = 1;
const MAX_ZOOM = 3;

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

const ProfileAvatarCropper = ({
  imageSrc,
  open,
  saving = false,
  title = 'Adjust photo',
  hint = 'Drag to reposition. Use the slider to zoom.',
  zoomLabel = 'Zoom',
  cancelLabel = 'Cancel',
  saveLabel = 'Save photo',
  savingLabel = 'Saving...',
  onCancel,
  onConfirm,
}) => {
  const [naturalSize, setNaturalSize] = useState({ width: 0, height: 0 });
  const [zoom, setZoom] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const dragRef = useRef(null);

  useEffect(() => {
    if (!open || !imageSrc) return undefined;

    setZoom(1);
    setOffset({ x: 0, y: 0 });
    setNaturalSize({ width: 0, height: 0 });

    const img = new Image();
    img.onload = () => {
      setNaturalSize({
        width: img.naturalWidth || img.width,
        height: img.naturalHeight || img.height,
      });
    };
    img.src = imageSrc;

    return undefined;
  }, [open, imageSrc]);

  const baseScale = useMemo(() => {
    if (!naturalSize.width || !naturalSize.height) return 1;
    return Math.max(FRAME_SIZE / naturalSize.width, FRAME_SIZE / naturalSize.height);
  }, [naturalSize]);

  const displayScale = baseScale * zoom;
  const displayWidth = naturalSize.width * displayScale;
  const displayHeight = naturalSize.height * displayScale;

  const maxOffsetX = Math.max(0, (displayWidth - FRAME_SIZE) / 2);
  const maxOffsetY = Math.max(0, (displayHeight - FRAME_SIZE) / 2);

  useEffect(() => {
    setOffset((prev) => ({
      x: clamp(prev.x, -maxOffsetX, maxOffsetX),
      y: clamp(prev.y, -maxOffsetY, maxOffsetY),
    }));
  }, [maxOffsetX, maxOffsetY, zoom]);

  const handlePointerDown = (event) => {
    if (saving) return;
    event.currentTarget.setPointerCapture?.(event.pointerId);
    dragRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      originX: offset.x,
      originY: offset.y,
    };
  };

  const handlePointerMove = (event) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    const nextX = drag.originX + (event.clientX - drag.startX);
    const nextY = drag.originY + (event.clientY - drag.startY);
    setOffset({
      x: clamp(nextX, -maxOffsetX, maxOffsetX),
      y: clamp(nextY, -maxOffsetY, maxOffsetY),
    });
  };

  const endDrag = (event) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    dragRef.current = null;
    try {
      event.currentTarget.releasePointerCapture?.(event.pointerId);
    } catch (_) {
      // Ignore if capture already released.
    }
  };

  const handleZoomChange = (event) => {
    setZoom(Number(event.target.value) || 1);
  };

  const handleSave = useCallback(async () => {
    if (!imageSrc || !naturalSize.width || saving) return;

    const image = await new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = reject;
      img.src = imageSrc;
    });

    const canvas = document.createElement('canvas');
    canvas.width = OUTPUT_SIZE;
    canvas.height = OUTPUT_SIZE;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const scale = (baseScale * zoom) * (OUTPUT_SIZE / FRAME_SIZE);
    const drawWidth = naturalSize.width * scale;
    const drawHeight = naturalSize.height * scale;
    const drawX = (OUTPUT_SIZE - drawWidth) / 2 + offset.x * (OUTPUT_SIZE / FRAME_SIZE);
    const drawY = (OUTPUT_SIZE - drawHeight) / 2 + offset.y * (OUTPUT_SIZE / FRAME_SIZE);

    ctx.fillStyle = '#0b1220';
    ctx.fillRect(0, 0, OUTPUT_SIZE, OUTPUT_SIZE);
    ctx.drawImage(image, drawX, drawY, drawWidth, drawHeight);

    const blob = await new Promise((resolve) => {
      canvas.toBlob((result) => resolve(result), 'image/jpeg', 0.92);
    });

    if (!blob || typeof onConfirm !== 'function') return;
    onConfirm(blob);
  }, [baseScale, imageSrc, naturalSize, offset.x, offset.y, onConfirm, saving, zoom]);

  if (!open || !imageSrc) return null;

  return (
    <div className="profile-avatar-cropper-backdrop" role="presentation" onClick={onCancel}>
      <div
        className="profile-avatar-cropper"
        role="dialog"
        aria-modal="true"
        aria-labelledby="profile-avatar-cropper-title"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="profile-avatar-cropper-header">
          <h3 id="profile-avatar-cropper-title">{title}</h3>
          <p>{hint}</p>
        </div>

        <div className="profile-avatar-cropper-stage">
          <div
            className="profile-avatar-cropper-frame"
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={endDrag}
            onPointerCancel={endDrag}
          >
            {naturalSize.width > 0 && (
              <img
                src={imageSrc}
                alt=""
                className="profile-avatar-cropper-image"
                draggable={false}
                style={{
                  width: `${displayWidth}px`,
                  height: `${displayHeight}px`,
                  transform: `translate(-50%, -50%) translate(${offset.x}px, ${offset.y}px)`,
                }}
              />
            )}
          </div>
        </div>

        <label className="profile-avatar-cropper-zoom">
          <span>{zoomLabel}</span>
          <input
            type="range"
            min={MIN_ZOOM}
            max={MAX_ZOOM}
            step="0.01"
            value={zoom}
            disabled={saving}
            onChange={handleZoomChange}
          />
        </label>

        <div className="profile-avatar-cropper-actions">
          <button
            type="button"
            className="profile-avatar-cropper-btn profile-avatar-cropper-btn--ghost"
            onClick={onCancel}
            disabled={saving}
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            className="profile-avatar-cropper-btn profile-avatar-cropper-btn--primary"
            onClick={handleSave}
            disabled={saving || !naturalSize.width}
          >
            {saving ? savingLabel : saveLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProfileAvatarCropper;
